import React, { useEffect, useRef, useState } from 'react';
import { calculateAge } from '../../utils/date';
import { XMarkIcon, PhotoIcon, CameraIcon } from '@heroicons/react/24/outline';
import {
  useCreateObservationMutation,
  useGetUploadUrlMutation,
  useListLearningGoalsQuery,
} from '../../store/api/localApi';
import { ObservationVisibility } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { THEME_LABELS, groupByThemeAndAim } from '../learning-goals/themes';
import { useToast } from '../Toast';
import ObservationPhotoUpload, { UploadingPhoto } from './ObservationPhotoUpload';

interface SessionSummary {
  id: string;
  className: string;
  scheduledStart: string;
  scheduledEnd: string;
  date: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  /** Child's DOB (ISO). Drives the under-6 gate on the Aistéar learning-goal
   *  picker — when absent or the child is 6+, the picker is hidden and no
   *  learningGoalIds are sent. */
  studentDateOfBirth?: string;
  /** The currently-selected session this observation is being captured against. */
  session: SessionSummary;
  /** Optional pickable list (Student Detail entry point — student may be in
   *  more than one session today; staff has to choose which). */
  sessionOptions?: SessionSummary[];
  onSessionChange?: (sessionId: string) => void;
}

const TEXT_LIMIT = 2000;
const MAX_PHOTOS = 4;

const ObservationCreateModal: React.FC<Props> = ({
  open,
  onClose,
  studentId,
  studentName,
  studentDateOfBirth,
  session,
  sessionOptions,
  onSessionChange,
}) => {
  const { showSuccess, showError } = useToast();
  const { activeOrganizationId } = useAuth();
  const [createObservation, { isLoading: isSaving }] = useCreateObservationMutation();
  const [getUploadUrl] = useGetUploadUrlMutation();

  const [text, setText] = useState('');
  const [photos, setPhotos] = useState<UploadingPhoto[]>([]);
  const [visibility, setVisibility] = useState<ObservationVisibility>(
    ObservationVisibility.PARENTS
  );
  const [learningGoalIds, setLearningGoalIds] = useState<string[]>([]);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Under-6 gate: only show the Aistéar learning-goal picker for a child whose
  // age can be computed and is < 6. When hidden, learningGoalIds stays [] and
  // nothing is sent — the backend rejects tags for over-6 children anyway.
  const age = calculateAge(studentDateOfBirth);
  const showLearningGoals = age !== null && age < 6;

  const { data: goalsData } = useListLearningGoalsQuery(
    { organizationId: activeOrganizationId || '' },
    { skip: !showLearningGoals || !activeOrganizationId }
  );
  const goalGroups = groupByThemeAndAim((goalsData?.goals ?? []).filter((g) => g.isActive));

  useEffect(() => {
    if (open) {
      setText('');
      setPhotos([]);
      setVisibility(ObservationVisibility.PARENTS);
      setLearningGoalIds([]);
      closeBtnRef.current?.focus();
    }
  }, [open, studentId, session.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const anyUploading = photos.some((p) => p.uploading);
  const canSubmit = text.trim().length > 0 && !anyUploading && !isSaving;

  if (!open) return null;

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const slots = MAX_PHOTOS - photos.length;
    const fresh = Array.from(files).slice(0, slots);

    for (const file of fresh) {
      const previewUrl = URL.createObjectURL(file);
      const next: UploadingPhoto = {
        s3Key: '',
        mimeType: file.type,
        sizeBytes: file.size,
        previewUrl,
        uploading: true,
      };
      setPhotos((p) => [...p, next]);
      try {
        const { uploadUrl, s3Key } = await getUploadUrl({
          purpose: 'OBSERVATION_PHOTO',
          contextId: session.id,
          mimeType: file.type,
          sizeBytes: file.size,
          fileName: file.name,
        }).unwrap();
        const putResp = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        if (!putResp.ok) throw new Error(`S3 ${putResp.status}`);
        setPhotos((p) =>
          p.map((q) => (q.previewUrl === previewUrl ? { ...q, s3Key, uploading: false } : q))
        );
      } catch {
        setPhotos((p) =>
          p.map((q) =>
            q.previewUrl === previewUrl ? { ...q, uploading: false, error: 'Failed' } : q
          )
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const result = await createObservation({
        sessionId: session.id,
        studentId,
        text: text.trim(),
        photos: photos
          .filter((p) => p.s3Key && !p.error)
          .map(({ s3Key, mimeType, sizeBytes, width, height }) => ({
            s3Key,
            mimeType,
            sizeBytes,
            width,
            height,
          })),
        visibility,
        learningGoalIds: showLearningGoals ? learningGoalIds : [],
      }).unwrap();
      const n = result.recipientCount;
      showSuccess(
        visibility === ObservationVisibility.PARENTS && n > 0
          ? `Sent to ${n} parent${n === 1 ? '' : 's'}.`
          : 'Observation saved.'
      );
      onClose();
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to save observation.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="observation-create-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="observation-create-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        />
        <div className="relative z-10 bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 id="observation-create-title" className="text-lg font-semibold text-gray-900">
                Observation about {studentName}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {session.className} · {session.date}
              </p>
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              data-testid="observation-create-cancel"
              className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {sessionOptions && sessionOptions.length > 1 && (
            <select
              data-testid="observation-create-session-picker"
              className="block w-full rounded-md border-gray-300 mb-4"
              value={session.id}
              onChange={(e) => onSessionChange?.(e.target.value)}
            >
              {sessionOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.className} · {s.scheduledStart}–{s.scheduledEnd}
                </option>
              ))}
            </select>
          )}

          <textarea
            data-testid="observation-create-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, TEXT_LIMIT))}
            placeholder={`Note about ${studentName}'s session…`}
            className="block w-full rounded-md border-gray-300 mb-3 h-32 resize-y"
          />

          <div className="mb-4">
            <ObservationPhotoUpload
              photos={photos}
              onRemove={(i) => {
                URL.revokeObjectURL(photos[i].previewUrl);
                setPhotos((p) => p.filter((_, idx) => idx !== i));
              }}
            />
            {photos.length < MAX_PHOTOS && (
              <div className="flex items-center gap-3 mt-2">
                {/* Take photo: rear camera on mobile (`capture`), file chooser
                    on desktop. */}
                <label
                  data-testid="observation-create-add-photo"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  <CameraIcon className="h-4 w-4" />
                  Take photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
                {/* Choose from gallery: no `capture` → photo library on mobile;
                    multiple allowed. */}
                <label
                  data-testid="observation-create-choose-photo"
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  <PhotoIcon className="h-4 w-4" />
                  Choose photo
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/heic"
                    multiple
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                  />
                </label>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {photos.length} / {MAX_PHOTOS} photos
            </p>
          </div>

          {showLearningGoals && goalGroups.length > 0 && (
            <div data-testid="observation-learning-goals" className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-1">Aistéar learning goals</p>
              <p className="text-xs text-gray-400 mb-2">
                Tag the goals this observation evidences (optional).
              </p>
              <div className="max-h-48 overflow-y-auto space-y-3 rounded-md border border-gray-200 p-3">
                {goalGroups.map((group) => (
                  <div key={group.theme}>
                    <p className="text-xs font-semibold text-gray-600">
                      {THEME_LABELS[group.theme]}
                    </p>
                    {group.aims.map((aim) => (
                      <div key={aim.aimNumber} className="mt-1 ml-1">
                        <p className="text-xs text-gray-400">{aim.aimText}</p>
                        {aim.goals.map((goal) => (
                          <label
                            key={goal.id}
                            className="flex items-start gap-2 py-0.5 text-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              data-testid={`observation-lg-${goal.id}`}
                              className="mt-0.5"
                              checked={learningGoalIds.includes(goal.id)}
                              onChange={(e) =>
                                setLearningGoalIds((prev) =>
                                  e.target.checked
                                    ? [...prev, goal.id]
                                    : prev.filter((x) => x !== goal.id)
                                )
                              }
                            />
                            <span className="text-gray-800">{goal.text}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          <fieldset className="mb-4">
            <legend className="text-sm font-medium text-gray-700 mb-1">Visibility</legend>
            <label className="inline-flex items-center mr-4">
              <input
                type="radio"
                data-testid="observation-create-visibility-parents"
                checked={visibility === ObservationVisibility.PARENTS}
                onChange={() => setVisibility(ObservationVisibility.PARENTS)}
              />
              <span className="ml-2 text-sm">Parents can see</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                data-testid="observation-create-visibility-staff"
                checked={visibility === ObservationVisibility.STAFF_ONLY}
                onChange={() => setVisibility(ObservationVisibility.STAFF_ONLY)}
              />
              <span className="ml-2 text-sm">Staff only</span>
            </label>
          </fieldset>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="observation-create-submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="px-3 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Posting…' : 'Post observation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObservationCreateModal;
